import { Menu, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import * as React from "react";

import { cn } from "../../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * A small, dependency-light "sidebar" primitive inspired by the shadcn/ui
 * sidebar block. Each piece below has a single responsibility:
 *  - SidebarProvider/useSidebar: own the collapsed/expanded (desktop) and
 *    open/closed (mobile/tablet) state, and persist the former.
 *  - Sidebar/SidebarContent/SidebarGroup*: pure layout/visual shells.
 *  - SidebarMenuButton: one clickable nav row, active/collapsed aware.
 *  - SidebarTrigger/SidebarMobileTrigger: the toggle buttons consumed from
 *    the desktop and mobile headers respectively.
 *
 * Responsive: at >=992px (this app's desktop breakpoint, matching
 * HeaderWebView's `min-[992px]:` split) the sidebar is a sticky, in-flow
 * icon+label rail that can collapse to icons-only. Below that it becomes a
 * fixed, off-canvas drawer with a backdrop, toggled from the mobile header -
 * replacing the old ModalMenuBar hamburger menu so navigation looks and
 * behaves the same at every screen size.
 */

const SIDEBAR_STORAGE_KEY = "np-sidebar-collapsed";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";
const DESKTOP_MEDIA_QUERY = "(min-width: 992px)";

const isDesktopViewport = (): boolean =>
  typeof window !== "undefined" && window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

type SidebarState = "expanded" | "collapsed";

interface SidebarContextValue {
  state: SidebarState;
  collapsed: boolean;
  toggleSidebar: () => void;
  setCollapsed: (collapsed: boolean) => void;
  mobileOpen: boolean;
  closeMobileSidebar: () => void;
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

const readPersistedCollapsedState = (): boolean => {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const persistCollapsedState = (collapsed: boolean): void => {
  try {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
  } catch {
    // localStorage can be unavailable (private browsing, disabled storage) - ignore.
  }
};

export const useSidebar = (): SidebarContextValue => {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({ children }: { children: React.ReactNode }) => {
  // Raw persisted desktop preference - only meaningful (and only ever
  // toggled) at >=992px. Kept separate from `effectiveCollapsed` below so a
  // collapsed desktop rail never also collapses the mobile drawer's labels.
  const [desktopCollapsed, setDesktopCollapsedState] = React.useState<boolean>(
    readPersistedCollapsedState
  );
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState<boolean>(isDesktopViewport);

  const setCollapsed = React.useCallback((value: boolean) => {
    setDesktopCollapsedState(value);
    persistCollapsedState(value);
  }, []);

  const closeMobileSidebar = React.useCallback(() => setMobileOpen(false), []);

  // One toggle, two meanings: on desktop it flips the collapsed/expanded
  // rail (persisted); below the desktop breakpoint it opens/closes the
  // off-canvas drawer instead. Callers (SidebarTrigger, SidebarMobileTrigger)
  // don't need to know which - each is only ever rendered in its own header
  // variant, so the viewport check here always matches.
  const toggleSidebar = React.useCallback(() => {
    if (isDesktopViewport()) {
      setDesktopCollapsedState(previous => {
        const next = !previous;
        persistCollapsedState(next);
        return next;
      });
    } else {
      setMobileOpen(previous => !previous);
    }
  }, []);

  // Cmd/Ctrl + B toggles the sidebar, matching the common editor convention.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // Track the breakpoint itself so rendering (not just the toggle) can react
  // to it. Crossing back to desktop also closes the drawer, so it isn't left
  // open (and mid-transition) once it's no longer rendered as an overlay.
  React.useEffect(() => {
    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
      if (event.matches) setMobileOpen(false);
    };
    mediaQueryList.addEventListener("change", handleChange);
    return () => mediaQueryList.removeEventListener("change", handleChange);
  }, []);

  // The rail only collapses to icons-only at >=992px - on the mobile/tablet
  // drawer it always shows full labels, regardless of the persisted desktop
  // preference.
  const effectiveCollapsed = desktopCollapsed && isDesktop;

  const value = React.useMemo<SidebarContextValue>(
    () => ({
      state: effectiveCollapsed ? "collapsed" : "expanded",
      collapsed: effectiveCollapsed,
      toggleSidebar,
      setCollapsed,
      mobileOpen,
      closeMobileSidebar
    }),
    [effectiveCollapsed, toggleSidebar, setCollapsed, mobileOpen, closeMobileSidebar]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

/**
 * Desktop trigger - collapses/expands the in-flow rail. Only ever rendered
 * from HeaderWebView (itself gated to >=992px), so `toggleSidebar` always
 * takes its desktop branch here.
 */
export const SidebarTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { collapsed, toggleSidebar } = useSidebar();

  return (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size="icon"
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      data-testid="sidebar-toggle-button"
      onClick={event => {
        onClick?.(event);
        toggleSidebar();
      }}
      className={cn(
        "tw-h-8 tw-w-8 tw-text-muted-foreground hover:tw-bg-muted hover:tw-text-foreground",
        className
      )}
      {...props}
    >
      {collapsed ? (
        <PanelLeftOpen className="tw-size-5" />
      ) : (
        <PanelLeftClose className="tw-size-5" />
      )}
    </Button>
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

/**
 * Mobile/tablet trigger - opens/closes the off-canvas drawer. Only ever
 * rendered from HeaderMobileView (itself gated to <992px), so `toggleSidebar`
 * always takes its mobile branch here.
 */
export const SidebarMobileTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { mobileOpen, toggleSidebar } = useSidebar();

  return (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size="icon"
      aria-label={mobileOpen ? "Close menu" : "Open menu"}
      title={mobileOpen ? "Close menu" : "Open menu"}
      data-testid="sidebar-mobile-toggle-button"
      onClick={event => {
        onClick?.(event);
        toggleSidebar();
      }}
      className={cn(
        "tw-h-9 tw-w-9 tw-text-muted-foreground hover:tw-bg-muted hover:tw-text-foreground",
        className
      )}
      {...props}
    >
      {mobileOpen ? <X className="tw-h-5 tw-w-5" /> : <Menu className="tw-h-5 tw-w-5" />}
    </Button>
  );
});
SidebarMobileTrigger.displayName = "SidebarMobileTrigger";

interface SidebarProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Full viewport height. At >=992px it's sticky and sits as an in-flow flex
 * sibling of the header+content column (see MainContentViewLayout), never
 * stacked below the header, so it always spans edge-to-edge on the left and
 * collapses to an icon-only rail. Below that it's a fixed off-canvas drawer
 * that slides in over the content, with a backdrop to dismiss it - the
 * `contentHide`/wrapper div this used to need in MainContentViewLayout is
 * gone, since this component now owns its own visibility at every width.
 */
export const Sidebar = ({ children, className }: SidebarProps) => {
  const { collapsed, mobileOpen, closeMobileSidebar } = useSidebar();

  return (
    <>
      {/* Backdrop: mobile/tablet only (auto-hidden at >=992px), dismisses the drawer */}
      <div
        aria-hidden="true"
        onClick={closeMobileSidebar}
        className={cn(
          "tw-scope tw-fixed tw-inset-0 tw-z-40 tw-bg-black/40 tw-transition-opacity tw-duration-200 min-[992px]:tw-hidden",
          mobileOpen ? "tw-opacity-100" : "tw-pointer-events-none tw-opacity-0"
        )}
      />
      <aside
        data-testid="app-sidebar"
        data-state={collapsed ? "collapsed" : "expanded"}
        className={cn(
          "tw-scope tw-fixed tw-inset-y-0 tw-left-0 tw-z-50 tw-flex tw-h-screen tw-w-64 tw-flex-col tw-border-r tw-border-sidebar-border tw-bg-sidebar tw-text-sidebar-foreground",
          "tw-transition-transform tw-duration-200 tw-ease-linear tw-overflow-x-hidden tw-overflow-y-auto",
          mobileOpen ? "tw-translate-x-0" : "-tw-translate-x-full",
          "min-[992px]:tw-sticky min-[992px]:tw-top-0 min-[992px]:tw-z-auto min-[992px]:tw-translate-x-0",
          "min-[992px]:tw-transition-[width] min-[992px]:tw-duration-200 min-[992px]:tw-ease-linear",
          collapsed ? "min-[992px]:tw-w-[68px]" : "min-[992px]:tw-w-60",
          className
        )}
      >
        <div className="tw-flex tw-h-14 tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-sidebar-border tw-px-3 min-[992px]:tw-hidden">
          <span className="tw-text-sm tw-font-semibold">Menu</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close menu"
            onClick={closeMobileSidebar}
            className="tw-h-8 tw-w-8 tw-text-sidebar-foreground hover:tw-bg-sidebar-accent hover:tw-text-sidebar-accent-foreground"
          >
            <X className="tw-h-4 tw-w-4" />
          </Button>
        </div>
        {children}
      </aside>
    </>
  );
};

export const SidebarContent = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2", className)}>
    {children}
  </div>
);

export const SidebarGroup = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-p-2", className)}>{children}</div>
);

export const SidebarGroupLabel = ({ children }: { children: React.ReactNode }) => {
  const { collapsed } = useSidebar();

  if (collapsed) return null;

  return (
    <div className="tw-flex tw-h-8 tw-shrink-0 tw-items-center tw-rounded-md tw-px-2 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-gray-500">
      {children}
    </div>
  );
};

export const SidebarGroupContent = ({ children }: { children: React.ReactNode }) => (
  <div className="tw-w-full tw-text-sm">{children}</div>
);

export const SidebarMenu = ({ children }: { children: React.ReactNode }) => (
  <ul className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-1">{children}</ul>
);

export const SidebarMenuItem = ({ children }: { children: React.ReactNode }) => (
  <li className="tw-relative tw-list-none">{children}</li>
);

interface SidebarMenuButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  isActive?: boolean;
  icon: React.ReactNode;
  label: string;
  className?: string;
}

export const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ isActive = false, icon, label, className, ...props }, ref) => {
    const { collapsed } = useSidebar();
    // Only relevant while collapsed - the popover stands in for the label
    // that the expanded rail already shows inline.
    const [hovered, setHovered] = React.useState(false);

    const button = (
      <button
        ref={ref}
        type="button"
        data-active={isActive}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          "tw-flex tw-w-full tw-items-center tw-gap-3 tw-overflow-hidden tw-rounded-md tw-p-2 tw-text-left tw-text-sm tw-transition-colors",
          "hover:tw-bg-sidebar-accent hover:tw-text-sidebar-accent-foreground",
          "data-[active=true]:tw-bg-sidebar-accent data-[active=true]:tw-font-medium data-[active=true]:tw-text-sidebar-accent-foreground",
          collapsed && "tw-justify-center tw-gap-0",
          className
        )}
        {...props}
      >
        <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center [&>svg]:tw-h-5 [&>svg]:tw-w-5">
          {icon}
        </span>
        {!collapsed && <span className="tw-truncate">{label}</span>}
      </button>
    );

    if (!collapsed) return button;

    return (
      <Popover open={hovered} onOpenChange={setHovered}>
        <PopoverTrigger asChild>{button}</PopoverTrigger>
        <PopoverContent
          side="right"
          align="center"
          sideOffset={8}
          className="tw-w-auto tw-whitespace-nowrap tw-rounded-md tw-border-0 tw-bg-foreground tw-p-0 tw-px-3 tw-py-1.5 tw-text-xs tw-text-background tw-shadow-md"
        >
          {label}
        </PopoverContent>
      </Popover>
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";
